import { createContext, useEffect, useRef, useState, useCallback, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AuthContextType, User, RegisterRequest, AUTH_ERROR_MESSAGES } from '@/types';
import { authService } from '@/services/auth.service';
import {
  setAccessToken,
  clearAccessToken,
  formatApiError,
  getAccessToken,
  isAuthRejection,
  refreshAccessToken,
  subscribeToRefreshBreaker,
} from '@/services/api';
import i18n from '@/i18n';
import { resetTracking } from '@/services/tracking';
import { devLog } from '@/utils/logger';
import { resetTeamScopedStores } from '@/lib/resetTeamScopedStores';
import { subscribeToAuthChanged, subscribeToAuthResync } from '@/services/authSync';

// Minimum gap between focus/visibility-triggered /me re-syncs.
const FOCUS_RESYNC_MIN_INTERVAL_MS = 5000;

/**
 * Authentication Context
 *
 * Manages global authentication state:
 * - Current user
 * - Loading state
 * - Login/Register/Logout methods
 * - Auto-initialize auth on app load
 */

// Module-level flags to prevent concurrent auth initialization
// (protects against React StrictMode double-mount)
let isInitializing = false;
let initPromise: Promise<void> | null = null;
// Cache for user fetched during init - allows second mount to reuse without extra API call
let cachedInitUser: User | null = null;
// Bridges a transient connectivity failure from the first (StrictMode) init
// to the second, waiting init — mirrors `cachedInitUser` for the error case.
let cachedInitConnectionError = false;

/** Shallow compare of the whole user plus a JSON compare of nested objects. */
const isSameIdentity = (a: User | null, b: User): boolean =>
  a !== null && JSON.stringify(a) === JSON.stringify(b);

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Set when the boot-time auth check fails due to a transient network/timeout
  // error (NOT a genuine 401/403 session rejection). While this is true, the
  // existing session must NOT be treated as "logged out" — PrivateRoute shows
  // a "can't reach the server" retry affordance instead of bouncing to /login.
  const [authConnectionError, setAuthConnectionError] = useState(false);
  // True while the api.ts refresh circuit breaker is OPEN (repeated non-auth
  // refresh failures mid-session — see api.ts `subscribeToRefreshBreaker`).
  // Deliberately a SEPARATE flag from `authConnectionError`: that one only
  // matters for the (bootstrap-only) full-screen ConnectionErrorScreen, which
  // is safe to show because nothing is mounted yet. `connectionDegraded` can
  // flip true at ANY point while the app is fully mounted (e.g. composer
  // open) — it must ONLY ever drive a non-destructive banner, never a route
  // guard, so it is intentionally not wired into PrivateRoute/RequireSuperAdmin.
  const [connectionDegraded, setConnectionDegraded] = useState(false);
  const lastSyncAtRef = useRef<number>(Date.now());

  // Bridge the interceptor's module-level circuit breaker into React state
  // so the app can show a "reconnecting" indicator mid-session instead of
  // silently hammering /auth/refresh forever (Bug B). Subscribing once for
  // the lifetime of the provider is enough — the breaker is a singleton.
  useEffect(() => {
    const unsubscribe = subscribeToRefreshBreaker((isOpen) => {
      setConnectionDegraded(isOpen);
    });
    return unsubscribe;
  }, []);

  /**
   * Initialize auth state on mount
   *
   * SECURITY NOTE: Access tokens are stored in memory only (lost on page refresh).
   * Refresh tokens are in httpOnly cookies (secure, not accessible to JavaScript).
   *
   * On page refresh:
   * 1. Access token is lost (memory cleared)
   * 2. Proactively call /refresh to get new access token (avoids 401 errors)
   * 3. Then call /me with the new token
   * 4. All subsequent requests work normally
   *
   * This approach prevents XSS attacks while maintaining good UX and avoiding wasteful 401 errors.
   */
  useEffect(() => {
    let isMounted = true; // Guard against race conditions

    const initAuth = async () => {
      // If already initializing, wait for existing initialization
      if (isInitializing && initPromise) {
        devLog.log('[AuthContext] Init already in progress, waiting...');
        await initPromise;

        // After waiting, handle the result (the original promise may have used a stale isMounted)
        // The original init's isMounted was set to false by StrictMode cleanup, so state wasn't set
        if (isMounted) {
          const currentPath = window.location.pathname;
          const isAuthPage =
            ['/login', '/register', '/forgot-password'].includes(currentPath) ||
            currentPath.startsWith('/auth/reset-password') ||
            currentPath === '/auth/magic' ||
            currentPath.startsWith('/Invitation/accept');

          const hasToken = getAccessToken();

          if (hasToken) {
            if (isAuthPage) {
              // On auth page with valid token - redirect to dashboard
              devLog.log('[AuthContext] Valid session found after waiting, redirecting to dashboard...');
              window.location.replace('/');
              return;
            } else {
              // On protected page with valid token - use cached user from first init
              if (cachedInitUser) {
                devLog.log('[AuthContext] Using cached user after waiting:', cachedInitUser.email);
                setUser(cachedInitUser);
                setAuthConnectionError(false);
                cachedInitUser = null; // Clear cache after use
              } else {
                // Fallback: fetch if cache is empty (shouldn't happen normally)
                devLog.log('[AuthContext] No cached user, fetching...');
                try {
                  const currentUser = await authService.getCurrentUser();
                  devLog.log('[AuthContext] User fetched after waiting:', currentUser.email);
                  setUser(currentUser);
                  setAuthConnectionError(false);
                } catch (error) {
                  // Only a genuine 401/403 means the session is actually invalid.
                  // A network error/timeout here must NOT log the user out.
                  if (isAuthRejection(error)) {
                    devLog.log('[AuthContext] Failed to fetch user after waiting (session rejected), clearing token');
                    clearAccessToken();
                    setUser(null);
                    setAuthConnectionError(false);
                  } else {
                    devLog.warn('[AuthContext] Failed to fetch user after waiting (connectivity issue), preserving session');
                    setAuthConnectionError(true);
                  }
                }
              }
            }
          } else if (cachedInitConnectionError) {
            // No token, but the FIRST init hit a transient connectivity error
            // (its own `isMounted` was already false when it happened, under
            // StrictMode, so it couldn't set state itself). Bridge that
            // outcome here instead of falling through to a false "no session"
            // state, which would otherwise incorrectly redirect to /login.
            devLog.warn('[AuthContext] First init hit a connectivity issue (bridged), preserving session');
            setAuthConnectionError(true);
          }

          // Consume the bridged flag so it can't leak into a later, unrelated init.
          cachedInitConnectionError = false;
          setIsLoading(false);
        }
        return;
      }

      // Set flag and create promise
      isInitializing = true;
      // Reset stale bridged state from any prior init before starting a new one.
      cachedInitConnectionError = false;

      initPromise = (async () => {
        devLog.log('[AuthContext] Initializing authentication...');

        const currentPath = window.location.pathname;
        const isAuthPage =
          ['/login', '/register', '/forgot-password'].includes(currentPath) ||
          currentPath.startsWith('/auth/reset-password') ||
            currentPath === '/auth/magic' ||
          currentPath.startsWith('/Invitation/accept');

        // On auth pages, check if user has valid refresh token
        if (isAuthPage) {
          devLog.log('[AuthContext] On auth page, checking for valid session...');
          try {
            // Attempt lightweight token check (refresh only)
            const { accessToken: newToken } = await authService.refreshToken();
            setAccessToken(newToken);
            devLog.log('[AuthContext] Valid session found, redirecting to dashboard...');

            // User is authenticated, redirect away from auth page
            if (isMounted) {
              window.location.replace('/');
            }
            return;
          } catch (error) {
            // No valid session (expected for users who need to login)
            devLog.log('[AuthContext] No valid session, staying on auth page');
            if (isMounted) {
              setUser(null);
              setIsLoading(false);
            }
            return;
          }
        }

        // Check if we already have a valid access token
        const existingToken = getAccessToken();

        try {
          // If no access token (page refresh), proactively refresh it first
          if (!existingToken) {
            devLog.log('[AuthContext] No access token found, attempting token refresh...');
            const { accessToken: newToken } = await authService.refreshToken();
            setAccessToken(newToken);
            devLog.log('[AuthContext] Access token refreshed successfully, token length:', newToken?.length);
          }

          // Verify token was actually set before making /me call
          const currentToken = getAccessToken();
          devLog.log('[AuthContext] Token before /me call:', currentToken ? `set (${currentToken.length} chars)` : 'NULL');

          // Now fetch current user (with valid token, no 401 expected)
          devLog.log('[AuthContext] Fetching current user...');
          const currentUser = await authService.getCurrentUser();
          devLog.log('[AuthContext] Session restored successfully:', currentUser.email);

          // Cache user for second mount (StrictMode) to avoid duplicate /me call
          cachedInitUser = currentUser;
          cachedInitConnectionError = false;

          if (isMounted) {
            setUser(currentUser);
            setAuthConnectionError(false);
            cachedInitUser = null; // Clear cache if we successfully set state

            // Apply user's preferred language on session restore
            const preferredLanguage = currentUser.preferredLanguage || 'en';
            if (preferredLanguage !== i18n.language) {
              devLog.log('[AuthContext] Applying language preference on session restore:', preferredLanguage);
              localStorage.setItem('preferredLanguage', preferredLanguage);
              await i18n.changeLanguage(preferredLanguage);
            }
          }
        } catch (error) {
          cachedInitUser = null; // Clear cache on error

          // A GENUINE auth rejection (server responded 401/403 to the refresh
          // or /me call) means the refresh cookie is actually invalid/expired
          // — log the user out exactly as before.
          //
          // A transient failure (network error / timeout, no `.response`)
          // means we simply couldn't verify the session right now — this is
          // the flaky-connection / PWA-cold-start case. The refresh cookie
          // may still be perfectly valid, so we must NOT clear the token or
          // log the user out. Surface a retryable connection error instead.
          if (isAuthRejection(error)) {
            devLog.log('[AuthContext] No valid session found (rejected by server)');
            clearAccessToken();
            cachedInitConnectionError = false;
            if (isMounted) {
              setUser(null);
              setAuthConnectionError(false);
            }
          } else {
            devLog.warn('[AuthContext] Auth check failed due to connectivity issue, preserving session:', error);
            // Set unconditionally (not just `if (isMounted)`) so a concurrent,
            // still-waiting second init (e.g. StrictMode double-invoke) can
            // pick up this outcome even though THIS invocation's `isMounted`
            // may already be false.
            cachedInitConnectionError = true;
            if (isMounted) {
              setAuthConnectionError(true);
            }
          }
        } finally {
          devLog.log('[AuthContext] Auth initialization complete');
          if (isMounted) {
            setIsLoading(false);
          }
        }
      })();

      try {
        await initPromise;
      } finally {
        // Reset flags after completion
        isInitializing = false;
        initPromise = null;
      }
    };

    initAuth();

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, []);

  /**
   * Login user with optional "remember me" support
   */
  const login = async (email: string, password: string, rememberMe = false): Promise<void> => {
    try {
      devLog.log('[AuthContext] Login called for:', email, 'rememberMe:', rememberMe);
      const response = await authService.login({ email, password, rememberMe });
      devLog.log('[AuthContext] Login response received:', response);

      await resetTracking(); // fresh tracking identity before the token swap
      setAccessToken(response.accessToken);
      devLog.log('[AuthContext] Access token set');

      // Login responses may not carry the Shop eligibility flag. Resolve /me before
      // navigation so a new account does not briefly land on the dashboard.
      const currentUser = await authService.getCurrentUser().catch(() => response.user);
      setUser(currentUser);
      setAuthConnectionError(false);
      devLog.log('[AuthContext] User state updated:', currentUser);

      // Apply user's language preference immediately after login
      const preferredLanguage = currentUser.preferredLanguage || 'en';
      if (preferredLanguage !== i18n.language) {
        devLog.log('[AuthContext] Applying language preference:', preferredLanguage);
        localStorage.setItem('preferredLanguage', preferredLanguage);
        await i18n.changeLanguage(preferredLanguage);
      }
    } catch (error) {
      devLog.error('[AuthContext] Login error:', error);
      const apiError = formatApiError(error);
      // Use user-friendly message if we have one for this error code
      const friendlyMessage = apiError.code ? AUTH_ERROR_MESSAGES[apiError.code] : null;
      throw new Error(friendlyMessage || apiError.message);
    }
  };

  /**
   * Register new user
   */
  const register = async (data: RegisterRequest): Promise<void> => {
    try {
      const response = await authService.register(data);
      await resetTracking();
      setAccessToken(response.accessToken);
      setUser(await authService.getCurrentUser().catch(() => response.user));
      setAuthConnectionError(false);
    } catch (error) {
      const apiError = formatApiError(error);
      // Use user-friendly message if we have one for this error code
      const friendlyMessage = apiError.code ? AUTH_ERROR_MESSAGES[apiError.code] : null;
      throw new Error(friendlyMessage || apiError.message);
    }
  };

  /**
   * Logout user
   */
  const logout = async (): Promise<void> => {
    try {
      await resetTracking(); // flush the logging-out identity while its token is still valid
      await authService.logout();
    } catch (error) {
      // Even if logout fails on backend, clear local state
      devLog.error('Logout error:', error);
    } finally {
      // Clear all React Query cache to prevent data leakage between users
      queryClient.clear();
      resetTeamScopedStores();

      // Clear all team/user-specific localStorage items
      // Patterns: selected_calendar_ids_*, post-composer-draft-*, ai_briefing_settings_*
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (
          key.startsWith('selected_calendar_ids') ||
          key.startsWith('post-composer-draft-') ||
          key.startsWith('ai_briefing_settings_')
        )) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));

      clearAccessToken();
      setUser(null);
    }
  };

  /**
   * Refresh current user data
   *
   * SECURITY: Also detects effective-team changes (God Mode enter/exit) and
   * evicts team-scoped React Query caches when the team actually changed, so
   * a previous tenant's cached responses can never leak into the new team's
   * view. This is defense-in-depth on top of the service worker no longer
   * caching authenticated `/api/*` GETs.
   */
  const refreshUser = async (): Promise<User> => {
    const previousEffectiveTeamId = user?.currentTeamId ?? user?.teamId ?? null;
    lastSyncAtRef.current = Date.now();
    try {
      const currentUser = await authService.getCurrentUser();
      // Skip setUser when nothing identity-relevant changed: a fresh object
      // would re-run every user-keyed effect/memo app-wide on each focus.
      if (!isSameIdentity(user, currentUser)) {
        setUser(currentUser);
      }

      const newEffectiveTeamId = currentUser.currentTeamId ?? currentUser.teamId ?? null;
      if (newEffectiveTeamId !== previousEffectiveTeamId) {
        await onTeamContextChange(previousEffectiveTeamId);
      }
      return currentUser;
    } catch (error) {
      const apiError = formatApiError(error);
      throw new Error(apiError.message);
    }
  };

  // Always-latest refreshUser for long-lived listeners (avoids stale `user` closure).
  const refreshUserRef = useRef(refreshUser);
  refreshUserRef.current = refreshUser;
  const isAuthenticatedRef = useRef(false);
  isAuthenticatedRef.current = !!user && !isLoading;

  // Re-sync /me when a stale tab/window regains focus, and when another tab
  // enters/exits God Mode. Without this, `user.godModeSession` (per-window
  // memory) can outlive the real server-side session.
  useEffect(() => {
    const resync = (force: boolean) => {
      if (!isAuthenticatedRef.current) return;
      if (!force && Date.now() - lastSyncAtRef.current < FOCUS_RESYNC_MIN_INTERVAL_MS) return;
      refreshUserRef.current().catch((error) => {
        devLog.warn('[AuthContext] Background /me re-sync failed', error);
      });
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') resync(false);
    };
    const onFocus = () => resync(false);

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    const unsubscribe = subscribeToAuthChanged(() => resync(true));
    const unsubscribeResync = subscribeToAuthResync(() => resync(true));
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      unsubscribe();
      unsubscribeResync();
    };
  }, []);

  /**
   * Retry the auth check after a transient connection failure
   * (see `authConnectionError`).
   *
   * Used by the "Can't reach the server — Retry" screen shown for protected
   * routes when the boot-time refresh/`/me` call failed due to a network
   * error or timeout (NOT a genuine 401/403 rejection). The existing session
   * (access token, if any) is left untouched on failure — this simply tries
   * the same check again.
   *
   * Deliberately NOT auto-retried in a loop: the backend rotates the refresh
   * token on every successful refresh, so hammering this after a lost
   * response risks sending an already-rotated (dead) cookie. Retry is
   * user-initiated only.
   */
  const retryAuthInit = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      const existingToken = getAccessToken();
      if (!existingToken) {
        const { accessToken: newToken } = await authService.refreshToken();
        setAccessToken(newToken);
      }

      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
      setAuthConnectionError(false);

      const preferredLanguage = currentUser.preferredLanguage || 'en';
      if (preferredLanguage !== i18n.language) {
        localStorage.setItem('preferredLanguage', preferredLanguage);
        await i18n.changeLanguage(preferredLanguage);
      }
    } catch (error) {
      if (isAuthRejection(error)) {
        // Retry surfaced a genuine rejection this time — log out for real.
        clearAccessToken();
        setUser(null);
        setAuthConnectionError(false);
      } else {
        devLog.warn('[AuthContext] Retry failed due to connectivity issue, preserving session:', error);
        setAuthConnectionError(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Manually retry the connection after the mid-session refresh circuit
   * breaker (`connectionDegraded`) has opened.
   *
   * Deliberately NOT the same code path as `retryAuthInit`: that one flips
   * `isLoading` to true, which makes PrivateRoute render a full-screen
   * `<LoadingSpinner fullScreen />` — fine at boot (nothing mounted yet) but
   * DESTRUCTIVE mid-session, since it would unmount the whole app tree
   * (including an open PostCreator draft), recreating the exact data-loss
   * bug this fix exists to prevent. This function never touches `isLoading`
   * and never unmounts anything; it just attempts one fresh refresh and, on
   * success, asks React Query to refetch already-mounted active queries.
   *
   * `connectionDegraded` itself is NOT set here — it is driven entirely by
   * the `subscribeToRefreshBreaker` effect above, so it stays in lockstep
   * with the actual breaker state (closes on success, stays/re-opens on
   * failure) no matter which code path triggered the refresh attempt.
   */
  const retryConnection = useCallback(async (): Promise<void> => {
    try {
      await refreshAccessToken();
      await queryClient.refetchQueries({ type: 'active' });
    } catch (error) {
      if (isAuthRejection(error)) {
        // The refresh cookie is genuinely dead (expired/revoked while the
        // tab was open) — nothing left to preserve. Clear local session
        // state; PrivateRoute's existing `!isAuthenticated` check will
        // redirect to /login on the next render. This is not a takeover
        // triggered by this code path — it's the ordinary "logged out"
        // state the app already handles everywhere else.
        clearAccessToken();
        setUser(null);
      }
      // Non-auth failure: refreshAccessToken() already re-armed/extended the
      // breaker itself; `connectionDegraded` stays true via the subscription
      // above and the banner remains visible for another manual retry.
    }
  }, [queryClient]);

  /**
   * Login with a pre-fetched response (used after password set/reset)
   */
  const loginWithResponse = useCallback(async (accessToken: string, userData: User) => {
    await resetTracking();
    setAccessToken(accessToken);
    const currentUser = await authService.getCurrentUser().catch(() => userData);
    setUser(currentUser);
    setAuthConnectionError(false);

    // Apply user's language preference
    const preferredLanguage = currentUser.preferredLanguage || 'en';
    if (preferredLanguage !== i18n.language) {
      localStorage.setItem('preferredLanguage', preferredLanguage);
      await i18n.changeLanguage(preferredLanguage);
    }
  }, []);

  /**
   * Called when team context changes (entering/exiting God Mode)
   * Invalidates all team-scoped queries to force refetch with new context
   */
  const onTeamContextChange = useCallback(async (previousTeamId: number | null) => {
    devLog.log('[AuthContext] Team context changed, invalidating team queries');
    resetTeamScopedStores();

    // Invalidate all queries that might have team-specific data
    // This forces refetch with new currentTeamId
    await queryClient.invalidateQueries({ queryKey: ['team'] });
    await queryClient.invalidateQueries({ queryKey: ['no-team'] });

    // Also clear the previous team's team-specific localStorage
    // (keys are team-suffixed, e.g. `selected_calendar_ids_${userId}_${teamId}` — see Calendar.tsx)
    if (previousTeamId != null) {
      // Keys are `selected_calendar_ids_<userId>_<teamId>` (legacy: `selected_calendar_ids_<teamId>`)
      localStorage.removeItem(`selected_calendar_ids_${previousTeamId}`);
      const suffix = `_${previousTeamId}`;
      const stale: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('selected_calendar_ids_') && key.endsWith(suffix)) stale.push(key);
      }
      stale.forEach((key) => localStorage.removeItem(key));
    }
  }, [queryClient]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    authConnectionError,
    connectionDegraded,
    login,
    register,
    logout,
    refreshUser,
    loginWithResponse,
    onTeamContextChange,
    retryAuthInit,
    retryConnection,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
